import { useEffect, useMemo, useState } from 'react';
import React from 'react';

import { Modal, ModalActions, ModalNote } from '../../components/ui/Modal/Modal';
import { Button } from '../../components/ui/Button/Button';
import { DatePicker, TimePicker, fromIsoDate } from '../../components/ui/DatePicker';
import type { AppNotification, NotificationForm } from '../../entities/notifications/model/types';
import { nowYekb, stamp } from '../../entities/notifications/model/window';
import styles from './NotificationsPage.module.scss';
import { Switch } from '../../components/ui/Switch/Switch';

export interface NotificationModalProps {
    mode: 'new' | 'edit' | 'copy' | null;
    source: AppNotification | null;
    saving: boolean;
    onClose: () => void;
    onSubmit: (form: NotificationForm) => void;
}

const EMPTY: NotificationForm = {
    text: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    showNow: false,
};

const TITLES: Record<'new' | 'edit' | 'copy', string> = {
    new: 'Новое уведомление',
    edit: 'Редактирование уведомления',
    copy: 'Копирование уведомления',
};

export function NotificationModal({ mode, source, saving, onClose, onSubmit }: NotificationModalProps) {
    const [form, setForm] = useState<NotificationForm>(EMPTY);

    useEffect(() => {
        if (!mode) return;
        setForm(
            source
                ? {
                    text: source.text,
                    startDate: source.raw.startDate,
                    startTime: source.raw.startTime,
                    endDate: source.raw.endDate,
                    endTime: source.raw.endTime,
                    showNow: false,
                }
                : EMPTY,
        );
    }, [mode, source]);

    const canShowNow = mode === 'new' || mode === 'copy';
    const showNow = canShowNow && Boolean(form.showNow);

    const patch = (part: Partial<NotificationForm>) => setForm((prev) => ({ ...prev, ...part }));

    const handleStartDate = (startDate: string) =>
        patch({
            startDate,
            endDate: form.endDate && startDate && form.endDate < startDate ? '' : form.endDate,
        });

    const toggleShowNow = (next: boolean) =>
        patch(next ? { showNow: true, startDate: '', startTime: '' } : { showNow: false });

    const error = useMemo(() => {
        if (!form.text.trim()) return 'Укажите текст уведомления.';
        if (!form.endDate || !form.endTime) return 'Укажите дату и время завершения.';

        if (!showNow && (form.startDate || form.startTime) && (!form.startDate || !form.startTime)) {
            return 'Начало задаётся датой и временем сразу — либо включите «Показать сразу».';
        }

        const startsAt = showNow ? '' : stamp(form.startDate, form.startTime);
        const endsAt = stamp(form.endDate, form.endTime);

        if (startsAt && endsAt <= startsAt) return 'Завершение должно быть позже начала.';
        if (endsAt <= nowYekb()) return 'Завершение уже прошло — уведомление никто не увидит.';

        return null;
    }, [form, showNow]);

    if (!mode) return null;

    const canSubmit = !error && !saving;

    return (
        <Modal
            open
            onClose={onClose}
            width={560}
            title={TITLES[mode]}
            footer={
                <>
                    <ModalNote>
                        {mode === 'edit'
                            ? 'Уведомление увидят все пользователи в указанный период. Время — GMT+5. При сохранении запись пересоздаётся.'
                            : 'Уведомление увидят все пользователи в указанный период. Время указывается в GMT+5, а не в вашем часовом поясе.'}
                    </ModalNote>
                    <ModalActions>
                        <Button onClick={onClose} disabled={saving}>Отмена</Button>
                        <Button variant="primary" disabled={!canSubmit} onClick={() => onSubmit(form)}>
                            {saving ? 'Сохраняем…' : mode === 'edit' ? 'Сохранить' : 'Создать'}
                        </Button>
                    </ModalActions>
                </>
            }
        >
            <label className={styles.field}>
                <span>Текст уведомления</span>
                <textarea
                    value={form.text}
                    rows={4}
                    onChange={(e) => patch({ text: e.target.value })}
                    placeholder="Например: 25 июля с 22:00 до 23:00 сервис будет недоступен."
                />
            </label>

            {canShowNow && (
                <div className={styles.switchRow}>
                    <Switch
                        label="Показать сразу"
                        discription="Начало проставит сервер — уведомление появится у пользователей в течение минуты."
                        checked={showNow}
                        disabled={saving}
                        onChange={toggleShowNow}
                    />
                </div>
            )}

            {!showNow && (
                <div className={styles.fieldRow}>
                    <div className={styles.field}>
                        <span>Начало показа</span>
                        <DatePicker
                            value={form.startDate}
                            onChange={handleStartDate}
                            disabled={saving}
                            aria-label="Дата начала показа"
                        />
                    </div>
                    <div className={styles.field}>
                        <span>Время</span>
                        <TimePicker
                            value={form.startTime}
                            onChange={(startTime) => patch({ startTime })}
                            disabled={saving}
                            aria-label="Время начала показа"
                        />
                    </div>
                </div>
            )}

            <div className={styles.fieldRow}>
                <div className={styles.field}>
                    <span>Завершение</span>
                    <DatePicker
                        value={form.endDate}
                        onChange={(endDate) => patch({ endDate })}
                        minDate={!showNow && form.startDate ? fromIsoDate(form.startDate) : null}
                        disabled={saving}
                        aria-label="Дата завершения показа"
                    />
                </div>
                <div className={styles.field}>
                    <span>Время</span>
                    <TimePicker
                        value={form.endTime}
                        onChange={(endTime) => patch({ endTime })}
                        disabled={saving}
                        aria-label="Время завершения показа"
                    />
                </div>
            </div>

            {error && form.text.trim() ? <p className={styles.formError}>{error}</p> : null}
        </Modal>
    );
}